import { useState } from 'react'
import { Modal, Table, Button, Tag, Space, message } from 'antd'
import { useNavigate } from 'react-router-dom'
import {
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  MinusCircleOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons'
import { scrapeApi } from '../api/client'
import type { ScrapeStatus } from '../types'

interface MissingInfoItem {
  project_id: string
  project_name: string
  website: string
  missing: string[]
  scrape_type: string
  scrape_status?: ScrapeStatus
}

interface MissingInfoModalProps {
  visible: boolean
  data: MissingInfoItem[]
  onClose: () => void
  onRefresh?: () => void
}

const MissingInfoModal: React.FC<MissingInfoModalProps> = ({ visible, data, onClose, onRefresh }) => {
  const navigate = useNavigate()
  const [scraping, setScraping] = useState(false)

  const getMissingTypeText = (type: string) => {
    const typeMap: Record<string, string> = {
      email: '邮箱信息',
      twitter_handle: '推特账号',
      telegram_group: 'Telegram群组',
      telegram_admins: 'Telegram管理员',
    }
    return typeMap[type] || type
  }

  const getScrapeStatusTag = (status?: ScrapeStatus) => {
    if (!status || status === 'NOT_SCRAPED') {
      return <Tag icon={<MinusCircleOutlined />} color="default">未抓取</Tag>
    }
    if (status === 'SCRAPED_NO_DATA') {
      return <Tag icon={<CloseCircleOutlined />} color="warning">无数据</Tag>
    }
    return <Tag icon={<CheckCircleOutlined />} color="success">已抓取</Tag>
  }

  const columns = [
    {
      title: '项目名称',
      dataIndex: 'project_name',
      key: 'project_name',
      width: 200,
    },
    {
      title: '网站',
      dataIndex: 'website',
      key: 'website',
      width: 250,
      ellipsis: true,
    },
    {
      title: '缺失信息',
      dataIndex: 'missing',
      key: 'missing',
      width: 200,
      render: (missing: string[]) => (
        <Space direction="vertical" size="small">
          {missing.map((m) => (
            <Tag key={m} color="error">
              {getMissingTypeText(m)}
            </Tag>
          ))}
        </Space>
      ),
    },
    {
      title: '抓取状态',
      dataIndex: 'scrape_status',
      key: 'scrape_status',
      width: 120,
      render: (status: ScrapeStatus) => getScrapeStatusTag(status),
    },
  ]

  const handleQuickScrapeAll = async () => {
    setScraping(true)
    try {
      // Collect all project IDs and their missing types
      const projectIds = data.map(item => item.project_id)
      // Get all unique missing types
      const allMissingTypes = new Set<string>()
      data.forEach(item => {
        item.missing.forEach(type => allMissingTypes.add(type))
      })

      const result = await scrapeApi.batchScrape({
        project_ids: projectIds,
        scrape_types: Array.from(allMissingTypes),
        force: true, // Force re-scrape to ensure we get missing admin info
      })

      const successCount = result.success || 0
      const failedCount = result.failed || 0
      const totalCount = result.total || 0

      // Show detailed results in a modal
      Modal.info({
        title: '抓取完成',
        width: 800,
        content: (
          <div>
            <div style={{ marginBottom: 16 }}>
              <p>总数: {totalCount}</p>
              <p>成功: {successCount}</p>
              <p>失败: {failedCount}</p>
            </div>
            {result.results && result.results.length > 0 && (
              <div style={{ maxHeight: 400, overflow: 'auto' }}>
                <Table
                  dataSource={result.results}
                  size="small"
                  pagination={false}
                  columns={[
                    {
                      title: '项目',
                      dataIndex: 'project_name',
                      key: 'project_name',
                      width: 150,
                    },
                    {
                      title: '邮箱',
                      key: 'email',
                      width: 100,
                      render: (_: any, record: any) => {
                        if (!record.email) return '-'
                        const { status, data, message: msg } = record.email
                        if (status === 'success') {
                          return <Tag color="success" title={data}>✓ {data}</Tag>
                        } else if (status === 'no_data') {
                          return <Tag color="warning">无数据</Tag>
                        } else {
                          return <Tag color="error" title={msg}>✗</Tag>
                        }
                      },
                    },
                    {
                      title: '推特',
                      key: 'twitter',
                      width: 100,
                      render: (_: any, record: any) => {
                        if (!record.twitter) return '-'
                        const { status, data, message: msg } = record.twitter
                        if (status === 'success') {
                          return <Tag color="success" title={`@${data}`}>✓ @{data}</Tag>
                        } else if (status === 'no_data') {
                          return <Tag color="warning">无数据</Tag>
                        } else {
                          return <Tag color="error" title={msg}>✗</Tag>
                        }
                      },
                    },
                    {
                      title: 'Telegram',
                      key: 'telegram',
                      width: 100,
                      render: (_: any, record: any) => {
                        if (!record.telegram) return '-'
                        const { status, data, message: msg } = record.telegram
                        if (status === 'success') {
                          return <Tag color="success" title={data}>✓ 群组</Tag>
                        } else if (status === 'no_data') {
                          return <Tag color="warning">无数据</Tag>
                        } else {
                          return <Tag color="error" title={msg}>✗</Tag>
                        }
                      },
                    },
                  ]}
                  rowKey="project_id"
                />
              </div>
            )}
          </div>
        ),
        onOk: () => {
          // Refresh and close
          if (onRefresh) {
            onRefresh()
          }
          onClose()
        },
      })
    } catch (error: any) {
      message.error(`抓取失败: ${error.message || '未知错误'}`)
    } finally {
      setScraping(false)
    }
  }

  const handleGoToScrape = () => {
    // 跳转到抓取页面
    navigate('/scrape')
    onClose()
  }

  return (
    <Modal
      title={
        <Space>
          <ExclamationCircleOutlined style={{ color: '#faad14' }} />
          <span>信息不完整，无法创建触达任务</span>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      width={800}
      footer={[
        <Button key="cancel" onClick={onClose} disabled={scraping}>
          关闭
        </Button>,
        <Button key="quick-scrape" icon={<ThunderboltOutlined />} onClick={handleQuickScrapeAll} loading={scraping}>
          一键抓取全部
        </Button>,
        <Button key="scrape" type="primary" onClick={handleGoToScrape} disabled={scraping}>
          前往抓取页面
        </Button>,
      ]}
    >
      <div style={{ marginBottom: 16 }}>
        <p>
          以下 <strong>{data.length}</strong> 个项目缺少必要的信息，请先抓取相关信息后再创建触达任务：
        </p>
      </div>
      <Table
        columns={columns}
        dataSource={data}
        rowKey="project_id"
        pagination={false}
        scroll={{ y: 400 }}
        size="small"
      />
      <div style={{ marginTop: 16, color: '#666' }}>
        <p>💡 提示：</p>
        <ul style={{ margin: 0, paddingLeft: 20 }}>
          <li>点击"前往抓取页面"可以批量抓取这些项目的信息</li>
          <li>抓取完成后，返回触达中心重新创建任务</li>
          <li>如果某个项目确实没有对应信息（如无推特账号），可以选择其他触达渠道</li>
        </ul>
      </div>
    </Modal>
  )
}

export default MissingInfoModal
